/* eslint-disable react-hooks/exhaustive-deps */
import {
	type MetaDescriptor,
	type MetaFunction,
	useLoaderData,
} from '@remix-run/react'
import {
	type LoaderFunctionArgs,
	type ActionFunctionArgs,
	json,
} from '@remix-run/server-runtime'
// import { getClientIPAddress } from 'remix-utils/get-client-ip-address'
import userAgent from 'express-useragent'

import Chatscreen from '#app/features/chat/index.tsx'
import { createGlobalChatStore } from '#app/features/chat/store/globalChatStore.tsx'
import { requireUserId } from '#app/utils/auth.server.ts'
import { mixpanel } from '#app/utils/mixpanel.server.ts'
import { callStrapiServer } from '#server/chat/chat.server.ts'
import {
	createStrapiMessage,
	createStrapiConversation,
	editStrapiTitle,
	deleteStrapiConversation,
} from '#server/chat/create-message.ts'
import { createTitle } from '#server/chat/create-title.server.ts'
import { getLanguageIdFromPathname } from '#server/utils/getLanguageIdFromPathname.ts'
import { serverTranslations } from '#server/utils/serverTranslations.ts'
import { type State, type ConversationBackendData } from '#types/chat/index.ts'

export async function loader({ request }: LoaderFunctionArgs) {
	const t = await serverTranslations(request, ['chat'])
	const userId = await requireUserId(request)
	const languageId = getLanguageIdFromPathname(request)

	const results = await callStrapiServer<ConversationBackendData>({
		url: `conversations`,
		method: 'GET',
		params: {
			uniqueId: userId,
		},
		additionalQueryParams: '&pagination[start]=0&pagination[limit]=20',
	})

	const currentUrl = new URL(request.url)

	const frontendUrl = process.env.FRONTEND_URL

	return json({
		data: results,
		meta: {
			frontend_url: process.env.FRONTEND_URL,
			image_url: `${frontendUrl}/img/color-reading-logo.jpg`,
			current_url: currentUrl.origin + currentUrl.pathname,
			locale: languageId,
			title: t('meta.title'),
			description: t('meta.description'),
		},
	})
}

function filterTrueProperties(source: { [x: string]: any }) {
	return Object.keys(source).reduce(
		(acc, key) => {
			if (source[key] === true) {
				// Check if the property is true
				acc[key] = source[key]
			}
			return acc
		},
		{} as { [x: string]: any },
	)
}

export async function action({ request }: ActionFunctionArgs) {
	let formData = await request.formData()
	const userId = await requireUserId(request)
	let { _action, text, ...values } = Object.fromEntries(formData)
	const uaString = request.headers.get('User-Agent') || ''
	const source = userAgent.parse(uaString)

	let createdMessage = null

	if (_action === 'create_conversation') {
		const relevantDeviceInfo = filterTrueProperties(source)

		mixpanel.track('Start Short Conversation', {
			distinct_id: userId,
			timeStamp: new Date().toISOString(),
			browser: source.browser,
			os: source.os,
			platform: source.platform,
			...relevantDeviceInfo,
		})

		if (values.messages) {
			const createdConversationWithMessages = await createStrapiConversation({
				userId,
				conversationId: values.conversationId as string,
				messageId: values.messageId as string,
				title: values.title as string,
				messages: values.messages as any,
			})

			return json({
				type: 'create_new_assistant_message',
				createdConversation: createdConversationWithMessages,
			})
		}

		if (values.message) {
			const createdConversationWithMessage = await createStrapiConversation({
				userId,
				conversationId: values.conversationId as string,
				messageId: values.messageId as string,
				title: values.title as string,
				content: values.message as string,
			})

			return json({
				type: 'create_new_assistant_message',
				createdConversation: createdConversationWithMessage,
			})
		}
	}

	if (_action === 'new_title') {
		const newTitle = await createTitle({ text: values.message as string })

		if (newTitle.failedToFetch) {
			return json({ type: 'failed_to_fetch' })
		}

		await editStrapiTitle({
			conversationId: values.conversationId as string,
			title: newTitle.title as string,
			tokenCount: newTitle.tokenCount as number,
		})

		return json({ type: 'set_new_title', title: newTitle.title })
	}

	if (_action === 'edit_title') {
		const createdTitle = await editStrapiTitle({
			conversationId: values.conversationId as string,
			title: values.title as string,
		})

		if (createdTitle.error) {
			return json({ type: 'failed_to_fetch' })
		}

		return json({ type: 'title_edited' })
	}

	if (_action === 'user_message') {
		mixpanel.track('Started Intake', {
			distinct_id: userId,
			timeStamp: new Date().toISOString(),
		})
		const createdMessage = await createStrapiMessage(values as any, 'user')

		if (createdMessage.error) {
			return json({ type: 'failed_to_fetch' })
		}

		return json(createdMessage)
	}

	if (_action === 'delete_conversation') {
		const deletedConversation = await deleteStrapiConversation(
			values.conversationId as string,
		)

		if (deletedConversation.error) {
			return json({ type: 'failed_to_delete' })
		} else {
			if (values.conversationId === values.currentParam) {
				return json({
					type: 'conversation_deleted',
					redirect: 'chat',
					conversationId: values.conversationId,
				})
			}
			return json({
				type: 'conversation_deleted',
				conversationId: values.conversationId,
				redirect: null,
			})
		}
	}

	if (_action === 'load_more_conversations') {
		const results: any = await callStrapiServer<ConversationBackendData>({
			url: `conversations`,
			method: 'GET',
			params: {
				uniqueId: userId,
			},
			additionalQueryParams: `&pagination[start]=${values.start}&pagination[limit]=20`,
		})

		if (results.error) {
			return json({ type: 'failed_to_fetch' })
		}
		return json({ type: 'set_more_conversations', results })
	}

	return json({ createdMessage })
}

export const initialChatState: State = {
	conversationId: '',
	conversationList: [],
	currentTokenAmount: 0,
	error: '',
	typeOfView: 'session',
	currentMessageProcess: null,
	menuIsVisible: false,
	intakeStatus: '',
	messages: [],
	currentPagesLoaded: 1,
	isCurrentlyFetchingMoreConversations: false,
	totalConversations: 0,
	initiatedMessages: false,
	tokensLeft: 0,
	coinsLeft: 0,
	showEmptyChat: true,
	titleHasBeenSet: false,
	emptyChatId: '',
	isCurrentlyStreaming: false,
	currentChatContext:
		'This can be anything, like the ipip results or whatever. We will pass this to the chatbot',
}

export const useGlobalChatStore = createGlobalChatStore(initialChatState)

export default function ChatRoute() {
	const data = useLoaderData<typeof loader>()

	return <Chatscreen data={data.data} />
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
	const meta = data && data.meta

	if (!meta) {
		return [
			{
				title: 'Chat - Wiser',
				description:
					'Wiser bot is a chatbot that helps you understand your mental health and wellbeing',
			},
		]
	}

	const title = meta.title

	const description = meta.description

	const url = meta.current_url
	const locale = meta.locale
	const image = meta.image_url

	return [
		{
			title: `${title}`,
			description,
			canonical: url,
		},
		{ property: 'og:title', content: title },
		{ property: 'og:updated_time', content: new Date('2/23/2024').valueOf() },
		{ property: 'og:description', content: description },
		{ property: 'og:type', content: 'website' },
		{ property: 'og:url', content: url },
		{ property: 'og:locale', content: locale },
		{ property: 'og:image', content: image },
		{ property: 'og:image:alt', content: title },
		{ name: 'twitter:card', content: 'summary' },
		{ name: 'twitter:site', content: '@Wiser1135028' },
		{ name: 'twitter:creator', content: '@Wiser1135028' },
		{ name: 'twitter:name', content: title },
		{ name: 'twitter:title', content: title },
		{ name: 'twitter:description', content: description },
		{ name: 'twitter:image', content: image },
		{ rel: 'canonical', href: url },
	] as MetaDescriptor[]
}
